'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import api from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'

import { useAuthStore } from '@/context/auth.store'
import { useI18n } from '@/components/I18nProvider'
import { useRegistrationConfig } from '@/hooks/useRegistrationConfig'

// ────────────────────────────────────────────────────────────────────────
// Robust error parser (handles string or dict responses)
// ────────────────────────────────────────────────────────────────────────

type ParsedError = {
  code?: string
  message: string
  reason?: string
  status?: number
  retryAfterSeconds?: number
  details?: Array<{ loc?: Array<string | number>; msg?: string; type?: string }>
}

type RegisterErrorLike = {
  message?: string
  response?: {
    status?: number
    headers?: Record<string, unknown>
    data?: {
      code?: string
      reason?: string
      message?: string
      detail?: string
      details?: ParsedError['details']
    }
  }
}

function parseErrorResponse(err: RegisterErrorLike): ParsedError {
  const response = err?.response
  const data = response?.data ?? {}
  const status = response?.status

  // Extract retry-after header for rate limits
  const retryAfterHeader = response?.headers?.['retry-after']
  const retryAfterSeconds = retryAfterHeader
    ? parseInt(String(retryAfterHeader), 10)
    : undefined

  // Extract code and reason from response.data
  const code = data?.code ?? undefined
  const reason = data?.reason ?? undefined
  const message =
    data?.message ?? data?.detail ?? err?.message ?? 'Unexpected error'
  const details = Array.isArray(data?.details) ? data.details : undefined

  return {
    code,
    message: String(message).trim() || 'Unexpected error',
    reason,
    status,
    retryAfterSeconds: !isNaN(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds : undefined,
    details,
  }
}

type RegisterInput = {
  email: string
  password: string
  confirm_password: string
  invite_code?: string
  referral_code?: string
  security_phrase?: string
  terms_accepted: boolean
}

interface RegisterFormProps {
  referralFromUrl?: string | null
}

type RegisterFlowState =
  | 'generic'
  | 'already_exists'
  | 'pending_verification'
  | 'invite_required'
  | 'invalid_invite'
  | 'referral_error'
  | 'validation_error'
  | 'policy_blocked'
  | 'beta_limited'
  | 'rate_limited'

// Tracks whether the user has explicitly chosen to proceed without any referral.
// When true, referralFromUrl is ignored on submit so the user's choice is respected.

type RecoveryAction =
  | 'login'
  | 'resend_verification'
  | 'fix_invite'
  | 'fix_referral'
  | 'retry'
  | 'contact_support'

type RecoveryPlan = {
  primaryAction: RecoveryAction
  secondaryAction?: RecoveryAction
}

type RegistrationStatus = 'active' | 'pending_verification' | 'pending_approval' | null

const RESEND_COOLDOWN_SECONDS = 60
export function RegisterForm({ referralFromUrl = null }: RegisterFormProps) {
  const router = useRouter()
  const registerAccount = useAuthStore((state) => state.register)
  const [isLoading, setIsLoading] = useState(false)
  const [isBetaBlocked, setIsBetaBlocked] = useState(false)
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatus>(null)
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [flowState, setFlowState] = useState<RegisterFlowState | null>(null)
  const [flowMessage, setFlowMessage] = useState<string | null>(null)
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0)
  const rateLimitRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [emailDeliveryStatus, setEmailDeliveryStatus] = useState<string | null>(null)
  const [referralOptedOut, setReferralOptedOut] = useState(false)
  const { t } = useI18n()
  const regConfig = useRegistrationConfig()
  const mode = regConfig.data?.mode || 'public'
  const requireInvite = mode === 'invite'
  const requiresApproval = mode === 'admin_approve'
  const allowPublicSignup = Boolean(regConfig.data?.allow_public_signup)
  const requireReferralCode = Boolean(regConfig.data?.require_referral_code)
  const allowAdminInviteOnly = Boolean(regConfig.data?.allow_admin_invite_only)

  const schema = z
    .object({
      email: z.string().email(t({ es: 'Introduce un email valido.', en: 'Enter a valid email address.' })),
      password: z
        .string()
        .min(8, t({ es: 'La contrasena debe tener al menos 8 caracteres.', en: 'Password must be at least 8 characters.' })),
      confirm_password: z.string(),
      invite_code: z.string().optional(),
      referral_code: z.string().optional(),
      terms_accepted: z.boolean(),
    })
    .refine((values) => values.password === values.confirm_password, {
      message: t({ es: 'Las contrasenas no coinciden.', en: 'Passwords do not match.' }),
      path: ['confirm_password'],
    })
    .refine((values) => values.terms_accepted === true, {
      message: t({ es: 'Debes aceptar los terminos.', en: 'You must accept the terms.' }),
      path: ['terms_accepted'],
    })

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    setFocus,
    setValue,
    control,
  } = useForm<RegisterInput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      referral_code: referralFromUrl || '',
      terms_accepted: false,
    },
  })

  const termsAccepted = Boolean(useWatch({ control, name: 'terms_accepted' }))
  const watchedReferralCode = useWatch({ control, name: 'referral_code' })
  const watchedEmail = useWatch({ control, name: 'email' })
  const [referralValidationState, setReferralValidationState] = useState<'idle' | 'loading' | 'valid' | 'invalid'>(
    referralFromUrl ? 'loading' : 'idle'
  )
  const [policyMessage, setPolicyMessage] = useState<string | null>(null)
  const referralRequiredByPolicy = !allowPublicSignup && requireReferralCode && !allowAdminInviteOnly
  const referralCandidate = String(referralFromUrl || watchedReferralCode || '').trim()
  const referralSatisfied = !referralRequiredByPolicy || referralValidationState === 'valid'
  const canSubmit = isValid && termsAccepted && !isLoading && !isBetaBlocked && referralSatisfied && rateLimitCountdown === 0
  const formDisabled = isLoading || isBetaBlocked || rateLimitCountdown > 0

  useEffect(() => {
    let active = true
    let syncTimer: number | null = null
    if (!referralRequiredByPolicy) {
      syncTimer = window.setTimeout(() => {
        if (!active) return
        setReferralValidationState('idle')
        clearErrors('referral_code')
      }, 0)
      return () => {
        active = false
        if (syncTimer !== null) window.clearTimeout(syncTimer)
      }
    }
    if (!referralCandidate) {
      syncTimer = window.setTimeout(() => {
        if (!active) return
        setReferralValidationState('idle')
        setError('referral_code', {
          type: 'manual',
          message: t({ es: 'Introduce un codigo de invitacion valido.', en: 'Enter a valid referral code.' }),
        })
      }, 0)
      return () => {
        active = false
        if (syncTimer !== null) window.clearTimeout(syncTimer)
      }
    }

    // If the code is unchanged from the URL param it was already validated
    // server-side during the /r/[code] redirect — skip the redundant API call.
    if (referralFromUrl && referralCandidate.toUpperCase() === referralFromUrl.toUpperCase()) {
      syncTimer = window.setTimeout(() => {
        if (!active) return
        clearErrors('referral_code')
        setReferralValidationState('valid')
      }, 0)
      return () => {
        active = false
        if (syncTimer !== null) window.clearTimeout(syncTimer)
      }
    }

    syncTimer = window.setTimeout(() => {
      if (active) setReferralValidationState('loading')
    }, 0)
    const timer = window.setTimeout(async () => {
      try {
        const response = await api.post('/referrals/validate', { code: referralCandidate })
        if (!active) return
        if (response.data?.valid) {
          clearErrors('referral_code')
          setReferralValidationState('valid')
          setPolicyMessage(null)
          return
        }
        setReferralValidationState('invalid')
        setError('referral_code', {
          type: 'manual',
          message: t({ es: 'Codigo de invitacion no valido.', en: 'Referral code is not valid.' }),
        })
      } catch {
        if (!active) return
        setReferralValidationState('invalid')
        setError('referral_code', {
          type: 'manual',
          message: t({ es: 'No se pudo validar el codigo.', en: 'Unable to validate referral code.' }),
        })
      }
    }, 350)

    return () => {
      active = false
      if (syncTimer !== null) window.clearTimeout(syncTimer)
      window.clearTimeout(timer)
    }
  }, [clearErrors, referralCandidate, referralRequiredByPolicy, setError, t])

  // Cleanup cooldown intervals on unmount
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
      if (rateLimitRef.current) clearInterval(rateLimitRef.current)
    }
  }, [])

  // Reset resendStatus to idle once cooldown expires
  useEffect(() => {
    if (resendCooldown === 0 && resendStatus === 'sent') {
      const timer = window.setTimeout(() => setResendStatus('idle'), 0)
      return () => window.clearTimeout(timer)
    }
  }, [resendCooldown, resendStatus])

  useEffect(() => {
    if (flowState === 'invite_required' || flowState === 'invalid_invite') {
      setFocus('invite_code')
    }
  }, [flowState, setFocus])

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const startRateLimitCountdown = (seconds: number) => {
    const initialValue = Math.max(1, Math.min(seconds, 3600)) // Cap at 1 hour
    setRateLimitCountdown(initialValue)
    if (rateLimitRef.current) clearInterval(rateLimitRef.current)
    rateLimitRef.current = setInterval(() => {
      setRateLimitCountdown((prev) => {
        if (prev <= 1) {
          if (rateLimitRef.current) clearInterval(rateLimitRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleResend = async () => {
    if (!verificationEmail || resendCooldown > 0 || resendStatus === 'sending') return
    setResendStatus('sending')
    setResendMessage(null)
    try {
      await api.post('/auth/resend-verification', { email: verificationEmail })
      setResendStatus('sent')
      startResendCooldown()
    } catch {
      setResendStatus('idle')
      setResendMessage(
        t({
          es: 'No pudimos reenviar el enlace en este momento.',
          en: 'Unable to resend the verification link right now.',
        })
      )
    }
  }

  const getRecoveryPlan = (state: RegisterFlowState | null): RecoveryPlan | null => {
    switch (state) {
      case 'already_exists':
        return { primaryAction: 'login', secondaryAction: 'retry' }
      case 'pending_verification':
        return { primaryAction: 'resend_verification', secondaryAction: 'retry' }
      case 'invite_required':
        return { primaryAction: 'fix_invite', secondaryAction: 'contact_support' }
      case 'invalid_invite':
        return { primaryAction: 'fix_invite', secondaryAction: 'retry' }
      case 'referral_error':
        return { primaryAction: 'fix_referral', secondaryAction: 'retry' }
      case 'validation_error':
        return { primaryAction: 'retry' }
      case 'policy_blocked':
        return { primaryAction: 'contact_support' }
      case 'beta_limited':
        return { primaryAction: 'contact_support' }
      case 'rate_limited':
        return { primaryAction: 'retry' }
      default:
        return null
    }
  }

  const focusFirstInvalidField = () => {
    const priority: Array<keyof RegisterInput> = [
      'email',
      'password',
      'confirm_password',
      'invite_code',
      'referral_code',
      'terms_accepted',
    ]
    for (const field of priority) {
      if (errors[field]) {
        setFocus(field)
        const el = document.querySelector(`[name="${field}"]`) as HTMLElement | null
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        break
      }
    }
  }

  const onSubmit = async (data: RegisterInput) => {
    setIsLoading(true)
    setFlowState(null)
    setFlowMessage(null)
    setResendMessage(null)
    setPolicyMessage(null)
    clearErrors(['invite_code', 'referral_code'])
    try {
      if (requireInvite && !String(data.invite_code || '').trim()) {
        setFlowState('invite_required')
        setError('invite_code', {
          type: 'manual',
          message: 'Necesitas un código de invitación',
        })
        setIsLoading(false)
        return
      }
      // If user explicitly opted out, send no referral_code even if URL has one.
      const effectiveReferral = referralOptedOut
        ? undefined
        : (referralFromUrl || data.referral_code || undefined)
      const result = await registerAccount(data.email, data.password, {
        invite_code: data.invite_code,
        referral_code: effectiveReferral,
      })

      setEmailDeliveryStatus(result.email_delivery || 'unknown')

      if (result.status === 'pending_verification') {
        setVerificationEmail(data.email)
        setRegistrationStatus('pending_verification')
        setIsLoading(false)
        return
      }
      if (result.status === 'pending_approval' || requiresApproval) {
        setRegistrationStatus('pending_approval')
        setIsLoading(false)
        return
      }
      // active — show success screen instead of instant redirect
      setRegistrationStatus('active')
      setIsLoading(false)
    } catch (err) {
      const parsedErr = parseErrorResponse(err)
      const { code, message, reason, status, retryAfterSeconds, details } = parsedErr
      const messageNormalized = message.trim().toLowerCase()

      // Handle rate limit with countdown
      if (status === 429) {
        setFlowState('rate_limited')
        const retrySeconds = (retryAfterSeconds && retryAfterSeconds > 0) ? retryAfterSeconds : 30 // Fallback to 30s
        startRateLimitCountdown(retrySeconds)
      } else if (code) {
        // HOTFIX v3.4: Deterministic code-based error mapping
        switch (code) {
          case 'USER_ALREADY_EXISTS_VERIFIED':
            setFlowState('already_exists')
            break
          case 'USER_ALREADY_EXISTS_UNVERIFIED':
            setVerificationEmail(data.email)
            setRegistrationStatus('pending_verification')
            setResendMessage(
              t({
                es: 'Este email ya existe, pero aun no esta verificado. Te hemos dejado la opcion para reenviar el enlace.',
                en: 'This email already exists, but it is not verified yet. You can resend the verification link.',
              })
            )
            break
          case 'INVITE_REQUIRED':
            setFlowState('invite_required')
            setError('invite_code', {
              type: 'manual',
              message: 'Necesitas un código de invitación',
            })
            break
          case 'INVALID_INVITE':
            setFlowState('invalid_invite')
            setError('invite_code', {
              type: 'manual',
              message: message || t({ es: 'El código de invitación no es válido', en: 'This invitation code is not valid.' }),
            })
            break
          case 'REFERRAL_ERROR':
            setFlowState('referral_error')
            setFlowMessage(message)
            setError('referral_code', {
              type: 'manual',
              message,
            })
            break
          case 'POLICY_BLOCKED':
            setPolicyMessage(
              t({
                es: 'El registro esta restringido actualmente. Usa un codigo valido o una invitacion de administrador.',
                en: 'Registration is currently restricted. Use a valid referral code or an admin invite.',
              })
            )
            setFlowState('policy_blocked')
            break
          case 'BETA_ACCESS_DENIED':
            setIsBetaBlocked(true)
            setFlowState('beta_limited')
            break
          case 'RATE_LIMITED':
            setFlowState('rate_limited')
            {
              const retrySeconds = (retryAfterSeconds && retryAfterSeconds > 0) ? retryAfterSeconds : 30
              startRateLimitCountdown(retrySeconds)
            }
            break
          case 'VALIDATION_ERROR':
            setFlowState('validation_error')
            if (Array.isArray(details)) {
              for (const item of details) {
                const field = Array.isArray(item?.loc) ? String(item.loc[item.loc.length - 1] || '') : ''
                const msg = String(item?.msg || 'Revisa los campos')
                if (field === 'email' || field === 'password' || field === 'confirm_password' || field === 'invite_code' || field === 'referral_code' || field === 'terms_accepted') {
                  setError(field, { type: 'manual', message: msg })
                }
              }
            }
            window.setTimeout(() => focusFirstInvalidField(), 0)
            break
          default:
            setFlowState('generic')
            break
        }
      } else {
        // Legacy fallback when code is not provided (backward compatibility)
        if (status === 400 && reason === 'unverified') {
          setVerificationEmail(data.email)
          setRegistrationStatus('pending_verification')
          setResendMessage(
            t({
              es: 'Este email ya existe, pero aun no esta verificado. Te hemos dejado la opcion para reenviar el enlace.',
              en: 'This email already exists, but it is not verified yet. You can resend the verification link.',
            })
          )
        } else if (status === 400 && reason === 'verified') {
          setFlowState('already_exists')
        } else if (status === 400 && messageNormalized === 'invite code required') {
          setFlowState('invite_required')
          setError('invite_code', {
            type: 'manual',
            message: 'Necesitas un código de invitación',
          })
        } else if (status === 400 && (
          messageNormalized === 'invalid or expired invite code' ||
          messageNormalized.includes('invitation has expired') ||
          messageNormalized.includes('invitation has already been') ||
          messageNormalized.includes('invitation is no longer') ||
          messageNormalized.includes('invite code')
        )) {
          setFlowState('invalid_invite')
          setError('invite_code', {
            type: 'manual',
            message: message || t({ es: 'El código de invitación no es válido', en: 'This invitation code is not valid.' }),
          })
        } else if (
          status === 400 &&
          (messageNormalized === 'invalid or expired referral code' ||
            messageNormalized === 'self referral is not allowed' ||
            messageNormalized === 'this email has already been referred')
        ) {
          setFlowState('referral_error')
          setFlowMessage(message)
          setError('referral_code', { type: 'manual', message })
        } else if (status === 422) {
          setFlowState('validation_error')
          if (Array.isArray(details)) {
            for (const item of details) {
              const field = Array.isArray(item?.loc) ? String(item.loc[item.loc.length - 1] || '') : ''
              const msg = String(item?.msg || 'Revisa los campos')
              if (field === 'email' || field === 'password' || field === 'confirm_password' || field === 'invite_code' || field === 'referral_code' || field === 'terms_accepted') {
                setError(field, { type: 'manual', message: msg })
              }
            }
          }
          window.setTimeout(() => focusFirstInvalidField(), 0)
        } else if (status === 403 && messageNormalized === 'registration_restricted') {
          setFlowState('policy_blocked')
          setPolicyMessage(
            t({
              es: 'El registro esta restringido actualmente. Usa un codigo valido o una invitacion de administrador.',
              en: 'Registration is currently restricted. Use a valid referral code or an admin invite.',
            })
          )
        } else if (status === 403) {
          setFlowState('policy_blocked')
        } else {
          setFlowState('generic')
        }
      }
      setIsLoading(false)
    }
  }

  // ── Post-registration screens ──────────────────────────────────────────────

  if (registrationStatus === 'active') {
    return (
      <div className="w-full space-y-6 text-center">
        <div className="space-y-2">
          <div className="text-4xl">🎉</div>
          <h3 className="text-xl font-semibold text-ink">
            {t({ es: '¡Cuenta creada!', en: 'Account created!' })}
          </h3>
          <p className="text-sm text-muted">
            {t({
              es: 'Ya puedes acceder a tu cuenta.',
              en: 'You can now access your account.',
            })}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          className="w-full"
          onClick={() => router.push('/dashboard')}
        >
          {t({ es: 'Ir al dashboard', en: 'Go to dashboard' })}
        </Button>
      </div>
    )
  }

  if (registrationStatus === 'pending_verification') {
    const recoveryPlan = getRecoveryPlan('pending_verification')
    return (
      <div className="w-full space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-ink mb-2">
            {t({ es: 'Revisa tu email', en: 'Check your email' })}
          </h3>
          <p className="text-sm text-muted">
            {t({
              es: `Enviamos un email de verificacion a ${verificationEmail}`,
              en: `We sent a verification email to ${verificationEmail}`,
            })}
          </p>
          <p className="text-xs text-muted mt-1">
            {t({
              es: 'Si no lo ves, revisa tu carpeta de spam.',
              en: "If you don't see it, check your spam folder.",
            })}
          </p>
        </div>
        <div className="rounded-lg border border-black/10 bg-blue-50/50 p-3 text-xs text-muted">
          {t({ es: 'El enlace de verificacion expira en 24 horas.', en: 'The verification link expires in 24 hours.' })}
        </div>
        {emailDeliveryStatus === 'failed' && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <strong>{t({ es: '⚠️ Aviso:', en: '⚠️ Note:' })}</strong> {t({ es: 'Hubo un problema al enviar el email. Por favor usa el botón "Reenviar" a continuación.', en: 'We had trouble sending the email. Please use the "Resend" button below.' })}
          </div>
        )}
        {resendStatus === 'sent' ? (
          <p className="text-sm text-emerald-700 text-center">
            {t({ es: 'Email de verificacion enviado correctamente.', en: 'Verification email sent successfully.' })}
          </p>
        ) : null}
        {resendMessage ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {resendMessage}
          </div>
        ) : null}
        <Button
          type="button"
          variant="primary"
          className="w-full"
          isLoading={resendStatus === 'sending'}
          disabled={resendStatus === 'sending' || resendCooldown > 0}
          autoFocus
          onClick={() => void handleResend()}
        >
          {resendStatus === 'sending'
            ? t({ es: 'Enviando...', en: 'Sending...' })
            : resendStatus === 'sent' && resendCooldown > 0
              ? t({ es: `Reenviado ✓ (${resendCooldown}s)`, en: `Resent ✓ (${resendCooldown}s)` })
              : t({ es: 'Reenviar verificacion', en: 'Resend verification email' })}
        </Button>
        {recoveryPlan?.secondaryAction === 'retry' ? (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              setRegistrationStatus(null)
              setVerificationEmail(null)
              setResendMessage(null)
              setResendStatus('idle')
            }}
          >
            {t({ es: 'Cambiar email', en: 'Change email' })}
          </Button>
        ) : null}
        <Link
          href="/login"
          className="inline-flex w-full items-center justify-center rounded-full border border-black/15 px-4 py-2 text-sm hover:bg-black/5"
        >
          {t({ es: 'Volver al login', en: 'Back to login' })}
        </Link>
      </div>
    )
  }

  if (registrationStatus === 'pending_approval') {
    return (
      <div className="w-full space-y-6 text-center">
        <div className="space-y-2">
          <div className="text-4xl">📋</div>
          <h3 className="text-xl font-semibold text-ink">
            {t({ es: 'Solicitud recibida', en: 'Request received' })}
          </h3>
          <p className="text-sm text-muted">
            {t({
              es: 'Tu solicitud de acceso ha sido enviada correctamente. Nuestro equipo la revisará en breve y recibirás un email cuando tu cuenta esté lista.',
              en: 'Your access request has been submitted. Our team will review it shortly and you will receive an email once your account is ready.',
            })}
          </p>
        </div>
        {emailDeliveryStatus === 'failed' && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <strong>{t({ es: '⚠️ Aviso:', en: '⚠️ Note:' })}</strong> {t({ es: 'Hubo un problema al enviar el email de confirmación. Te contactaremos a través de otros medios si es necesario.', en: 'We had trouble sending the confirmation email. We will contact you through other means if needed.' })}
          </div>
        )}
        <div className="rounded-lg border border-black/10 bg-gray-50 p-4 text-sm text-muted text-left space-y-1">
          <p>{t({ es: '• El proceso de revisión puede tardar 1-2 días hábiles.', en: '• The review process may take 1–2 business days.' })}</p>
          <p>{t({ es: '• No es necesario que hagas nada más por ahora.', en: '• No further action is needed from you at this time.' })}</p>
        </div>
        <Link
          href="/login"
          className="inline-flex w-full items-center justify-center rounded-full border border-black/15 px-4 py-2 text-sm hover:bg-black/5"
        >
          {t({ es: 'Volver al login', en: 'Back to login' })}
        </Link>
      </div>
    )
  }

  // ── Registration form ──────────────────────────────────────────────────────

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event)
      }}
      className="space-y-3"
    >
          {referralFromUrl && !flowState ? (
            <div className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800">
              {t({ es: 'Codigo de invitacion aplicado', en: 'Invitation code applied' })}
            </div>
          ) : null}

          {referralRequiredByPolicy && !flowState ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              {t({
                es: 'El registro esta disponible solo con invitacion. Usa un codigo de referral valido para continuar.',
                en: 'Registration is currently invite-only. Please use a valid referral code to continue.',
              })}
            </div>
          ) : null}

          {policyMessage && !flowState ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {policyMessage}
            </div>
          ) : null}

          {isBetaBlocked ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p>{t({ es: 'El acceso esta limitado por ahora.', en: 'Access is currently limited.' })}</p>
              <p className="mt-1">{t({ es: 'Estamos incorporando usuarios por fases.', en: "We're onboarding users in phases." })}</p>
              <div className="mt-3">
                <Link href="/waitlist">
                  <Button type="button" variant="outline" className="w-full">
                    {t({ es: 'Unirme a la lista de espera', en: 'Join the waitlist' })}
                  </Button>
                </Link>
              </div>
            </div>
          ) : null}

          {!isBetaBlocked && flowState === 'already_exists' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
              <div>
                <p className="font-semibold">{t({ es: 'Ya tienes una cuenta con este email', en: 'You already have an account with this email' })}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  autoFocus
                  onClick={() => router.push(`/login?email=${encodeURIComponent(watchedEmail || '')}`)}
                >
                  {t({ es: 'Iniciar sesion', en: 'Log in' })}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => router.push('/forgot-password')}
                >
                  {t({ es: 'Recuperar contrasena', en: 'Forgot password' })}
                </Button>
              </div>
            </div>
          ) : null}
          {!isBetaBlocked && flowState === 'policy_blocked' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold">{t({ es: 'El registro esta restringido', en: 'Registration is restricted' })}</p>
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <Link href="/waitlist" className="underline">
                  {t({ es: 'Unirme a la lista de espera', en: 'Join waitlist' })}
                </Link>
                <Link href="/contact" className="underline">
                  {t({ es: 'Contactar soporte', en: 'Contact support' })}
                </Link>
              </div>
            </div>
          ) : null}
          {!isBetaBlocked && flowState === 'invite_required' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold">Necesitas un código de invitación</p>
              <p className="mt-1">{t({ es: 'Necesitas una invitacion para continuar.', en: 'You need an invitation to continue.' })}</p>
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <button
                  type="button"
                  className="underline"
                  onClick={() => setFocus('invite_code')}
                >
                  {t({ es: 'Introducir codigo', en: 'Enter code' })}
                </button>
                <Link href="/contact" className="underline">
                  {t({ es: 'Contactar soporte', en: 'Contact support' })}
                </Link>
              </div>
            </div>
          ) : null}
          {!isBetaBlocked && flowState === 'invalid_invite' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold">El código de invitación no es válido</p>
              <p className="mt-1">{t({ es: 'Verifica el codigo o solicita uno nuevo.', en: 'Verify the code or request a new one.' })}</p>
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <button
                  type="button"
                  className="underline"
                  onClick={() => setFocus('invite_code')}
                >
                  {t({ es: 'Corregir codigo', en: 'Fix code' })}
                </button>
                <button
                  type="button"
                  className="underline"
                  onClick={() => {
                    setValue('invite_code', '')
                    clearErrors('invite_code')
                    setFocus('invite_code')
                  }}
                >
                  {t({ es: 'Limpiar campo', en: 'Clear input' })}
                </button>
              </div>
            </div>
          ) : null}
          {!isBetaBlocked && flowState === 'referral_error' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold">{flowMessage || t({ es: 'Error en el codigo de invitacion.', en: 'Referral code error.' })}</p>
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <button
                  type="button"
                  className="underline"
                  onClick={() => setFocus('referral_code')}
                >
                  {t({ es: 'Corregir referral', en: 'Fix referral' })}
                </button>
                {!referralRequiredByPolicy ? (
                  <button
                    type="button"
                    className="underline"
                    onClick={() => {
                      setValue('referral_code', '')
                      clearErrors('referral_code')
                      setFlowState(null)
                      setReferralOptedOut(true)
                    }}
                  >
                    {t({ es: 'Continuar sin referral', en: 'Continue without referral' })}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
          {!isBetaBlocked && flowState === 'validation_error' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-semibold">Revisa los campos</p>
              <p className="mt-1">{t({ es: 'Corrige los campos marcados e intentalo de nuevo.', en: 'Fix highlighted fields and try again.' })}</p>
            </div>
          ) : null}
          {!isBetaBlocked && flowState === 'rate_limited' ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
              <p className="font-semibold">
                {t({
                  es: 'Demasiados intentos de registro',
                  en: 'Too many registration attempts',
                })}
              </p>
              {rateLimitCountdown > 0 ? (
                <>
                  <p>
                    {t({
                      es: 'Intenta de nuevo en:',
                      en: 'Try again in:',
                    })}
                  </p>
                  <div className="font-mono text-lg font-semibold text-amber-700">
                    {String(Math.floor(rateLimitCountdown / 60)).padStart(2, '0')}:
                    {String(rateLimitCountdown % 60).padStart(2, '0')}
                  </div>
                  <p className="text-xs text-amber-800">
                    {t({
                      es: 'El botón se habilitará automáticamente cuando el contador llegue a cero.',
                      en: 'The button will be enabled automatically when the counter reaches zero.',
                    })}
                  </p>
                </>
              ) : (
                <p>
                  {t({
                    es: 'Espera un momento e intentalo de nuevo.',
                    en: 'Please wait and try again.',
                  })}
                </p>
              )}
            </div>
          ) : null}
          {!isBetaBlocked && flowState === 'generic' ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              <p className="font-semibold">{t({ es: 'Algo salio mal.', en: 'Something went wrong.' })}</p>
              <p className="mt-1">{t({ es: 'Por favor, intentalo de nuevo.', en: 'Please try again.' })}</p>
            </div>
          ) : null}

          <Input
            label={t({ es: 'Email', en: 'Email' })}
            type="email"
            placeholder={t({ es: 'tu@email.com', en: 'you@example.com' })}
            error={errors.email?.message}
            disabled={formDisabled}
            className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
            {...register('email')}
          />

          <Input
            label={t({ es: 'Contrasena', en: 'Password' })}
            type="password"
            placeholder={t({ es: 'Crea una contrasena segura', en: 'Create a secure password' })}
            error={errors.password?.message}
            disabled={formDisabled}
            className="rounded-2xl border-black/0 bg-[#f2f7f7] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:bg-white focus:ring-[#14b8a6]/10"
            {...register('password')}
          />

          <Input
            label={t({ es: 'Confirmar contrasena', en: 'Confirm Password' })}
            type="password"
            placeholder="********"
            error={errors.confirm_password?.message}
            disabled={formDisabled}
            className="rounded-2xl border-black/0 bg-[#f2f7f7] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:bg-white focus:ring-[#14b8a6]/10"
            {...register('confirm_password')}
          />

          {requireInvite ? (
            <Input
              label={t({ es: 'Codigo de registro', en: 'Invite code' })}
              placeholder="ABCD-1234-EFGH-5678"
              error={errors.invite_code?.message}
              disabled={formDisabled}
              className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
              {...register('invite_code')}
            />
          ) : null}

          {!requireInvite && !referralFromUrl ? (
            <Input
              label={
                referralRequiredByPolicy
                  ? t({ es: 'Codigo de invitacion', en: 'Referral code' })
                  : t({ es: 'Codigo de invitacion (opcional)', en: 'Invitation code (optional)' })
              }
              placeholder={t({ es: 'Introduce tu codigo', en: 'Enter your invite code' })}
              error={errors.referral_code?.message}
              helperText={
                referralValidationState === 'loading'
                  ? t({ es: 'Validando codigo...', en: 'Validating code...' })
                  : referralValidationState === 'valid'
                    ? t({ es: 'Codigo valido.', en: 'Referral code validated.' })
                    : undefined
              }
              disabled={formDisabled}
              className="rounded-2xl border-[#99f6e4] bg-[#f0fdfa] px-4.5 py-3 text-base shadow-none focus:border-[#14b8a6] focus:ring-[#14b8a6]/10"
              {...register('referral_code')}
            />
          ) : null}

          <div>
            <label
              className={[
                'flex cursor-pointer items-start gap-3 rounded-2xl border px-3 py-3 text-sm transition-colors',
                termsAccepted
                  ? 'border-[#0f766e] bg-[#f0fdfa] text-[#134e4a]'
                  : 'border-[#d8d2ca] bg-white/70 text-muted hover:border-[#99f6e4] hover:bg-[#f8fffd]',
                formDisabled ? 'cursor-not-allowed opacity-60' : '',
              ].join(' ')}
            >
              <input
                type="checkbox"
                className="sr-only"
                disabled={formDisabled}
                {...register('terms_accepted')}
              />
              <span
                className={[
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[13px] font-bold transition-colors',
                  termsAccepted
                    ? 'border-[#0f766e] bg-[#0f766e] text-white shadow-[0_8px_18px_-10px_rgba(15,118,110,0.8)]'
                    : 'border-[#b9b0a6] bg-white text-transparent',
                ].join(' ')}
                aria-hidden="true"
              >
                ✓
              </span>
              <span className="leading-relaxed">
                {t({ es: 'Acepto los ', en: 'I agree to the ' })}
                <Link href="/terms" className="text-accent hover:underline">
                  {t({ es: 'Terminos', en: 'Terms' })}
                </Link>
                {t({ es: ' y la ', en: ' and ' })}
                <Link href="/privacy" className="text-accent hover:underline">
                  {t({ es: 'Politica de Privacidad', en: 'Privacy Policy' })}
                </Link>
              </span>
            </label>
            <p className="mt-1 min-h-[1.25rem] text-sm text-red-600">{errors.terms_accepted?.message || '\u00A0'}</p>
          </div>

          <Button type="submit" variant="primary" size="lg" className="mt-2 w-full rounded-2xl bg-[linear-gradient(135deg,#0f766e,#14b8a6_58%,#2dd4bf)] py-3 text-[15px] shadow-[0_22px_50px_-28px_rgba(13,148,136,0.56)] hover:translate-y-0" isLoading={isLoading} disabled={!canSubmit}>
            {isLoading ? t({ es: 'Creando...', en: 'Creating...' }) : t({ es: 'Crear cuenta', en: 'Create account' })}
          </Button>

          <p className="pt-1 text-center text-sm text-muted">
            {t({ es: 'Ya tienes cuenta?', en: 'Already have an account?' })}{' '}
            <Link href="/login" className="font-medium text-[#0f766e] hover:underline">
              {t({ es: 'Iniciar sesion', en: 'Log in' })}
            </Link>
          </p>
    </form>
  )
}
